import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, Bank, CreditCard, SealCheck } from "@phosphor-icons/react";
import axios from "axios";
import Seo from "../components/Seo";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const Field = ({ label, value, onChange, testid, required }) => (
  <div>
    <div className="term-label mb-1">{label}{required && " *"}</div>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3" />
  </div>
);

const StatusBanner = ({ children, testid }) => (
  <div className="hard-border border-cyber-green text-cyber-green p-5 font-mono-tech text-sm flex items-center gap-3" data-testid={testid}>
    <SealCheck size={20} weight="bold" /> {children}
  </div>
);

export default function SponsorAgreement() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ company_legal_name: "", address: "", phone: "", signed_name: "", signed_title: "", accept: false });
  const [busy, setBusy] = useState(false);
  const [wire, setWire] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);

  const loadAgreement = useCallback(() => api.get(`/agreements/${token}`).then(({ data }) => setA(data)).catch((e) => setErr(e?.response?.status === 410 ? e.response.data.detail : "Agreement not found or link expired.")), [token]);
  useEffect(() => { loadAgreement(); }, [loadAgreement]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/agreements/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { clearInterval(t); setPolling(false); loadAgreement(); }
        else if (tries > 10) { clearInterval(t); setPolling(false); }
      } catch { clearInterval(t); setPolling(false); }
    }, 2000);
    return () => clearInterval(t);
  }, [sessionId, loadAgreement]);

  const sign = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(`/agreements/${token}/sign`, form);
      setA(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not sign. Check required fields."); }
    finally { setBusy(false); }
  };

  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/agreements/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { setErr(e?.response?.data?.detail || "Could not start payment."); setBusy(false); }
  };

  const payWire = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/agreements/${token}/wire`);
      setWire(data); loadAgreement();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not fetch wire instructions."); }
    finally { setBusy(false); }
  };

  if (err && !a) return <main className="grain min-h-screen flex items-center justify-center font-mono-tech text-cyber-red" data-testid="agreement-error-page">/ {err}</main>;
  if (!a) return <main className="grain min-h-screen flex items-center justify-center font-mono-tech text-white/40">/ loading agreement...</main>;

  const signed = ["signed", "wire_pending", "paid"].includes(a.status);

  return (
    <main className="grain min-h-screen" data-testid="sponsor-agreement-page">
      <Seo path={`/sponsor-agreement/${token}`} title="Sponsorship Agreement — FintechOmni" description="Review, sign and pay for your FintechOmni sponsorship online." noindex />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <div className="term-label mb-3">/ sponsorship agreement</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]">FintechOmni.</h1>
        <p className="font-mono-tech text-xs text-white/60 mt-4">{a.event_line}</p>

        <div className="hard-border bg-cyber-surface p-6 mt-10 space-y-2" data-testid="agreement-summary">
          <div className="term-label mb-3">/ sponsorship package &amp; pricing</div>
          <div className="flex justify-between text-sm"><span className="text-white/60">Sponsor company</span><span className="font-bold">{a.sponsor_company}</span></div>
          <div className="flex justify-between text-sm"><span className="text-white/60">Package</span><span className="font-bold">{a.package_label}</span></div>
          <div className="flex justify-between text-sm"><span className="text-white/60">Event</span><span>{a.event_title}</span></div>
          {a.appendix_notes && <div className="flex justify-between text-sm gap-6"><span className="text-white/60 whitespace-nowrap">Agreed terms</span><span className="text-right">{a.appendix_notes}</span></div>}
          <div className="flex justify-between text-lg border-t border-cyber-line pt-3 mt-3"><span className="text-white/60">Total fee</span><span className="font-black text-cyber-yellow" data-testid="agreement-amount">${Number(a.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} USD</span></div>
          <p className="text-white/40 text-xs pt-2">Payment: full USD payment within 7 calendar days after signing and before benefits/entry, unless otherwise stated in the sponsorship package above.</p>
        </div>

        <div className="mt-10">
          <div className="term-label mb-4">/ terms &amp; conditions (v{a.terms_version})</div>
          <div className="hard-border bg-cyber-black/60 p-6 max-h-[420px] overflow-y-auto space-y-4" data-testid="agreement-terms">
            {a.terms_sections.map((s) => (
              <div key={s.h}>
                <div className="font-bold text-sm text-white">{s.h}</div>
                <p className="text-white/60 text-sm leading-relaxed mt-1 whitespace-pre-line">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        {!signed && (
          <div className="hard-border bg-cyber-surface p-6 mt-10 space-y-4" data-testid="sign-block">
            <div className="term-label">/ sign electronically</div>
            <Field label="Sponsor legal entity name" value={form.company_legal_name} onChange={(v) => setForm({ ...form, company_legal_name: v })} testid="sign-company" required />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="sign-address" />
              <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="sign-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your full legal name (signature)" value={form.signed_name} onChange={(v) => setForm({ ...form, signed_name: v })} testid="sign-name" required />
              <Field label="Title" value={form.signed_title} onChange={(v) => setForm({ ...form, signed_title: v })} testid="sign-title" required />
            </div>
            <label className="flex items-start gap-3 cursor-pointer text-sm text-white/80">
              <input type="checkbox" checked={form.accept} onChange={(e) => setForm({ ...form, accept: e.target.checked })} className="mt-1" data-testid="sign-accept" />
              <span>I confirm I have authority to bind the entity above, and I accept the sponsorship package above and all Terms &amp; Conditions. Typing my name constitutes my electronic signature.</span>
            </label>
            {err && <div className="font-mono-tech text-xs text-cyber-red" data-testid="sign-error">/ {err}</div>}
            <button onClick={sign} disabled={busy} className="btn-neon w-full justify-center" data-testid="sign-btn">
              <CheckCircle size={16} weight="bold" /> {busy ? "Signing..." : "Sign agreement"}
            </button>
          </div>
        )}

        {signed && (
          <div className="mt-10 space-y-6">
            <StatusBanner testid="signed-banner">
              Signed by {a.signed_name} ({a.signed_title}) for {a.company_legal_name} on {new Date(a.signed_at).toLocaleString()}
            </StatusBanner>

            {a.status === "paid" && (
              <StatusBanner testid="paid-banner">Payment received — you're confirmed. Our team will be in touch with next steps.</StatusBanner>
            )}

            {polling && <div className="font-mono-tech text-xs text-cyber-yellow" data-testid="payment-polling">/ confirming your payment...</div>}

            {a.status !== "paid" && !polling && (
              <div className="hard-border bg-cyber-surface p-6 space-y-4" data-testid="payment-block">
                <div className="term-label">/ payment — ${Number(a.amount).toLocaleString()} USD</div>
                {err && <div className="font-mono-tech text-xs text-cyber-red">/ {err}</div>}
                <div className="grid sm:grid-cols-2 gap-4">
                  <button onClick={payCard} disabled={busy} className="btn-neon justify-center" data-testid="pay-card-btn">
                    <CreditCard size={16} weight="bold" /> Pay by credit card
                  </button>
                  <button onClick={payWire} disabled={busy} className="btn-ghost-neon justify-center" data-testid="pay-wire-btn">
                    <Bank size={16} weight="bold" /> Get wire instructions
                  </button>
                </div>
                <div className="font-mono-tech text-xs text-white/50 space-y-1" data-testid="card-fee-note">
                  <div className="flex justify-between gap-4"><span>Credit card total (incl. {`2.9% + $0.30`} processing fee of ${Number(a.card_fee).toLocaleString(undefined, { minimumFractionDigits: 2 })})</span><span className="text-cyber-yellow font-bold" data-testid="card-total">${Number(a.card_total).toLocaleString(undefined, { minimumFractionDigits: 2 })} USD</span></div>
                  <div className="flex justify-between gap-4"><span>Wire transfer total (no processing fee)</span><span className="text-white font-bold">${Number(a.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} USD</span></div>
                </div>
                {wire && (
                  <div className="hard-border bg-cyber-black/60 p-5 font-mono-tech text-xs space-y-2" data-testid="wire-instructions">
                    <div className="term-label mb-2">/ wire transfer instructions</div>
                    {Object.entries({ Beneficiary: wire.wire.beneficiary, Bank: wire.wire.bank_name, "Account #": wire.wire.account_number, "Routing / ABA": wire.wire.routing_number, SWIFT: wire.wire.swift, "Bank address": wire.wire.bank_address }).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4"><span className="text-white/50">{k}</span><span className="text-white text-right">{v}</span></div>
                    ))}
                    <div className="flex justify-between gap-4 border-t border-cyber-line pt-2"><span className="text-white/50">Reference code</span><span className="text-cyber-yellow font-bold" data-testid="wire-reference">{wire.reference}</span></div>
                    <p className="text-white/40 pt-2">{wire.wire.reference_note} We'll confirm by email once the wire arrives.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
